import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSettings } from "../lib/settings";
import { api } from "../lib/api";

/**
 * Page identifier matching the admin "Show on pages" checkboxes.
 *  - `/`            → home
 *  - `/shop`        → shop
 *  - `/shop?category=jewelry` → jewelry (also matches shop)
 *  - `/product/...` → product
 *  - `/about`       → about
 *  - `/wishlist`    → wishlist
 *  - `/account`     → account
 * Banners targeted at "*" show on every page.
 */
function pageIdsFor(pathname, search) {
  const ids = [];
  if (pathname === "/" || pathname === "") ids.push("home");
  if (pathname.startsWith("/shop")) {
    ids.push("shop");
    const cat = new URLSearchParams(search).get("category");
    if (cat) ids.push(cat); // e.g. "jewelry", "ai-prompts"
  }
  if (pathname.startsWith("/product")) ids.push("product");
  if (pathname.startsWith("/about")) ids.push("about");
  if (pathname.startsWith("/wishlist")) ids.push("wishlist");
  if (pathname.startsWith("/account")) ids.push("account");
  return ids;
}

export default function PromoBanner() {
  const { settings } = useSettings();
  const { pathname, search } = useLocation();
  const [banners, setBanners] = useState([]);
  const [hasAny, setHasAny] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const ids = pageIdsFor(pathname, search);
    // Fetch every enabled banner once → filter in-memory by current page.
    // This also tells us whether the new multi-banner system is in use at all
    // so we can decide whether to apply the legacy single-banner fallback.
    api.get("/promo-banners")
      .then((r) => {
        if (cancelled) return;
        const all = r.data || [];
        setHasAny(all.length > 0);
        const matching = all
          .filter((b) => b.enabled && b.image)
          .filter((b) => (b.pages || []).some((p) => p === "*" || ids.includes(p)))
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setBanners(matching);
      })
      .catch(() => { setBanners([]); setHasAny(false); });
    return () => { cancelled = true; };
  }, [pathname, search]);

  // Backwards-compat fallback ONLY when the user hasn't created any new banners.
  // Once they create at least one in Admin → Banners, the legacy single banner
  // is ignored everywhere so per-page targeting works as expected.
  let active = banners[0];
  if (!active && !hasAny && settings?.promo_banner_enabled && settings.promo_banner_image) {
    active = {
      id: "_legacy",
      image: settings.promo_banner_image,
      eyebrow: settings.promo_banner_eyebrow,
      title: settings.promo_banner_title,
      subtitle: settings.promo_banner_subtitle,
      cta_label: settings.promo_banner_cta_label,
      cta_link: settings.promo_banner_cta_link,
      align: settings.promo_banner_align || "left",
    };
  }
  if (!active) return null;

  const align = (active.align || "left").toLowerCase();
  const overlayJustify =
    align === "right" ? "justify-end text-right" :
    align === "center" ? "justify-center text-center" :
    "justify-start text-left";

  const link = active.cta_link || "";
  const isExternal = /^https?:\/\//i.test(link);
  const cta = active.cta_label;

  return (
    <section
      data-testid="promo-banner"
      data-banner-id={active.id}
      className="relative w-full overflow-hidden bg-[#1A1815]"
    >
      <div
        className="relative w-full h-[420px] sm:h-[500px] lg:h-[620px] bg-cover bg-center"
        style={{ backgroundImage: `url(${active.image})` }}
      >
        <div
          className={`absolute inset-0 ${
            align === "right"
              ? "bg-gradient-to-l from-black/55 via-black/20 to-transparent"
              : align === "center"
              ? "bg-gradient-to-b from-black/20 via-black/40 to-black/60"
              : "bg-gradient-to-r from-black/60 via-black/25 to-transparent"
          }`}
        />

        <div className={`relative h-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 flex items-center ${overlayJustify}`}>
          <div className="max-w-xl">
            {active.eyebrow && (
              <div data-testid="promo-banner-eyebrow" className="uppercase tracking-[0.3em] text-[11px] text-[#F3E5AB] mb-4">
                {active.eyebrow}
              </div>
            )}
            {active.title && (
              <h2 data-testid="promo-banner-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
                {active.title}
              </h2>
            )}
            {active.subtitle && (
              <p
                data-testid="promo-banner-subtitle"
                className="mt-5 text-sm sm:text-base text-white/85 max-w-md leading-relaxed"
                style={align === "center" ? { marginLeft: "auto", marginRight: "auto" } : undefined}
              >
                {active.subtitle}
              </p>
            )}
            {cta && link && (
              <div className="mt-8">
                {isExternal ? (
                  <a href={link} target="_blank" rel="noreferrer" data-testid="promo-banner-cta"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[var(--accent)] text-black text-sm font-semibold hover:opacity-90 transition">
                    {cta} <ArrowUpRight className="w-4 h-4" />
                  </a>
                ) : (
                  <Link to={link} data-testid="promo-banner-cta"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[var(--accent)] text-black text-sm font-semibold hover:opacity-90 transition">
                    {cta} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
